import React from 'react';
import {
  isShortTermBridge,
  isShortTermFixAndFlip,
  isLongTermRental,
  isRefinance,
} from '../../../../../utils/functions';
import { rentalInputs } from '../constants';

export const getMinLoanAmount = (values: any) => {
  if (isLongTermRental(values)) {
    return 125000
  } else {
    return 100000
  }
}

export const isBelowMinLoanAmount = (loanAmount: number, values: any) => {
  return getMinLoanAmount(values) > loanAmount
}

export function getInputData(values: any) {
  let data: any[] = []

  if (isShortTermBridge(values)) {
    if (!isRefinance(values)) {
      data = [
        {
          label: "Purchase Price",
          customKey: "price"
        }
      ]
    } else {
      data = [
        {
          label: "As-Is Value",
          customKey: "price"
        },
        {
          label: "Estimated payoff amount",
          customKey: "currentLoanBalance"
        }
      ]
    }
  } else if (isShortTermFixAndFlip(values)) {
    if (!isRefinance(values)) {
      data = [
        {
          label: "Purchase Price",
          customKey: "price"
        },
        {
          label: "Construction/rehab budget",
          customKey: "constructionBudget"
        },
        {
          label: "Approximate after repair value (ARV)",
          customKey: "afterRepairValue"
        },
      ]
    } else {
      data = [
        {
          label: "As-Is Value",
          customKey: "price"
        },
        {
          label: "Estimated payoff amount",
          customKey: "currentLoanBalance"
        },
        {
          label: "Construction/rehab budget",
          customKey: "constructionBudget"
        },
        {
          label: "Approximate after repair value (ARV)",
          customKey: "afterRepairValue"
        },
      ]
    }
  } else if (isLongTermRental(values)) {
    if (!isRefinance(values)) {
      data = [
        {
          label: "Purchase Price",
          customKey: "price"
        },
        ...rentalInputs
      ]
    } else {
      data = [
        {
          label: "As-Is Value",
          customKey: "price"
        },
        {
          label: "Estimated payoff amount",
          customKey: "currentLoanBalance"
        },
        ...rentalInputs
      ]
    }
  }

  return data
}

interface GetErrorsProps {
  label: any;
  customKey: string;
  values: any;
  labelText: any;
  loanAmount: number;
}

export function getErrors({ label, customKey, values, labelText, loanAmount }: GetErrorsProps) {
  const value = values[customKey]
  let error = undefined;

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });

  let max, min;
  const minARV = (values["price"] || 0) + (values["constructionBudget"] || 0);
  min = isShortTermFixAndFlip(values) && ["afterRepairValue"].includes(customKey) ? minARV : 15000;
  max = 10000000;

  let l = ""

  try {
    l = label.toLowerCase()
  } catch (e) {
    l = labelText
  }

  if (value === null || value === "") {
    error = <React.Fragment><span>Increase the {l}.</span> The {l} cannot be 0</React.Fragment>;
  } else {
    const isARentalInput = rentalInputs.some(({ customKey: k }) => {
      return k.includes(customKey)
    })

    const isMinExcluded = isARentalInput || ["currentLoanBalance"].includes(customKey)
    const isMaxExclued = ["price", "currentLoanBalance"].includes(customKey)

    if (value < min && !isMinExcluded) {
      error = <React.Fragment><span>Increase the {l}.</span> The {l} cannot be less than {currencyFormatter.format(min)}</React.Fragment>
    } else {
      if (value > max && !isMaxExclued) {
        error = <React.Fragment><span>Decrease the {l}.</span> The {l} cannot be greater than {currencyFormatter.format(max)}</React.Fragment>
      }
    }
  }

  if (["price"].includes(customKey) && loanAmount && values["price"]) {
    if (isBelowMinLoanAmount(loanAmount, values)) {
      if (isShortTermBridge(values)) {
        error = <React.Fragment><span>Increase the {l}.</span> The minimum loan amount is {currencyFormatter.format(getMinLoanAmount(values))}</React.Fragment>
      } else if (isShortTermFixAndFlip(values)) {
        error = <React.Fragment><span>Increase the purchase price / as is value or the construction budget.</span> The minimum loan amount is {currencyFormatter.format(getMinLoanAmount(values))}</React.Fragment>
      } else if (isLongTermRental(values)) {
        error = <React.Fragment><span>Increase the {l}.</span> The minimum loan amount is {currencyFormatter.format(getMinLoanAmount(values))}</React.Fragment>
      }
    } else {
      if (loanAmount > 2000000) {
        error = <React.Fragment><span>Decrease the {l}.</span> The maximum loan amount is {currencyFormatter.format(2000000)}</React.Fragment>
      }
    }
  }

  /* General Errors */

  if (isShortTermFixAndFlip(values)) {
    if (["afterRepairValue"].includes(customKey)) {
      const arv = values["afterRepairValue"]
      if (arv < minARV) {
        error = <React.Fragment><span>Increase the {l}, or decrease the purchase price/as is value or the construction budget.</span> The {l} must be greater than the sum of the purchase price/as is value and the rehab budget</React.Fragment>
      }
    } else if (["constructionBudget"].includes(customKey)) {
      const constructionBudget = values["constructionBudget"]
      const price = values["price"]
      if (constructionBudget && price && constructionBudget/price < 0.05) {
        error = <React.Fragment><span>Increase the {l} or decrease purchase price/as is value.</span> The {l} must be greater than 5% of the purchase price/as is value</React.Fragment>
      } else if (constructionBudget && price && constructionBudget > (1.5*price)) {
        error = <React.Fragment><span>Decrease the {l} or increase purchase price/as is value.</span> The {l} must be less than 150% of the purchase price/as is value</React.Fragment>
      }
    }
  }

  return error;
}

